import type { ConfirmFn } from '../seams';
import type { LoggerDispatch } from './state/loggerDraft';
import type { LoggerDraft } from './state/loggerDraftTypes';
import { clearPersistedLoggerState } from './state/loggerDraftStorage';

// Discard / close handler (Track R). No-op while a save is in flight. An
// empty draft closes immediately; a non-empty one prompts a destructive
// confirm before resetting + clearing the persisted state. Extracted
// verbatim out of LoggerScreen body.
//
// ⛔ The confirm goes through the SEAM, never react-native's Alert — this hook
// lives in packages/ui and `Alert` is a no-op on react-native-web (seams.ts
// states the contract). LG-42, Antenore's report of 2026-08-17: a workout with
// exercises in it could not be closed from the web AT ALL. The ✕ raised the
// native confirm, nothing appeared, and the branch that does the closing lives
// inside its onPress — so the button was simply dead. An EMPTY draft closes on
// the branch above without asking, which is why the first agent-driven run
// (which logged nothing before closing) reported the ✕ as working: the two
// cases take different paths and only the empty one was ever exercised
// (qa-harness.md §6). Reproduced against the real web build before the fix.
export function useDiscardWorkout({
  submitting,
  draft,
  dispatch,
  persistedHydratedRef,
  onClose,
  confirm,
  t,
}: {
  submitting: boolean;
  draft: LoggerDraft;
  dispatch: LoggerDispatch;
  persistedHydratedRef: { current: boolean };
  onClose: () => void;
  confirm: ConfirmFn;
  t: (k: string, opts?: Record<string, unknown>) => string;
}): () => void {
  return () => {
    if (submitting) return;
    if (draft.entries.length === 0) {
      dispatch({ type: 'reset' });
      void clearPersistedLoggerState();
      persistedHydratedRef.current = false;
      onClose();
      return;
    }
    // Fire-and-forget, same as the Alert it replaces: the ✕ handler has
    // nothing to await. A dismissed dialog resolves false (ConfirmFn's
    // contract), so walking away never discards the workout.
    void confirm({
      title: t('mobile.logger.discardTitle'),
      body: t('mobile.logger.discardBody'),
      confirmLabel: t('mobile.logger.discardConfirm'),
      cancelLabel: t('mobile.logger.discardCancel'),
    }).then((ok) => {
      if (!ok) return;
      dispatch({ type: 'reset' });
      void clearPersistedLoggerState();
      persistedHydratedRef.current = false;
      onClose();
    });
  };
}
